import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const tickerToSlug = (t: string) => t.toLowerCase().replace(/=/g, "-");

interface TrendingStock {
  ticker: string;
  company_name: string;
  price: number;
  change_percent: number;
  sentiment: string;
  updated_at: string;
}

const TrendingStocksWidget = () => {
  const [stocks, setStocks] = useState<TrendingStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchStocks = async () => {
      const { data } = await supabase
        .from("trending_stocks")
        .select("*")
        .order("change_percent", { ascending: false })
        .limit(10);

      if (!data || data.length === 0) {
        setLoading(false);
        return;
      }

      setStocks(data);
      setLoading(false);

      try {
        const tickers = data.map((s) => s.ticker);
        const { data: liveData, error } = await supabase.functions.invoke("live-quotes", {
          body: { tickers },
        });

        if (!error && liveData?.quotes) {
          const quoteMap: Record<string, { price: number; change_percent: number }> = {};
          for (const q of liveData.quotes) {
            if (q.price !== null) {
              quoteMap[q.ticker] = { price: q.price, change_percent: q.change_percent };
            }
          }

          setStocks((prev) =>
            prev.map((s) => {
              const live = quoteMap[s.ticker];
              if (!live) return s;
              return {
                ...s,
                price: live.price,
                change_percent: live.change_percent,
                sentiment: determineSentiment(live.change_percent),
              };
            })
          );
        }
      } catch {
        // Silently fall back to stored prices
      }
    };
    fetchStocks();
  }, []);

  if (loading || stocks.length === 0) return null;

  const hasContent = stocks.some((s) => s.change_percent !== 0);
  if (!hasContent) return null;

  // Duplicate the list to create a seamless circular loop
  const loopStocks = [...stocks, ...stocks];

  return (
    <section className="max-w-6xl mx-auto px-4 py-10">
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl font-display font-bold text-foreground">
          🔥 Trending Today
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Top movers | Live prices</p>
      </div>

      <div
        className="relative overflow-hidden group"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Edge fades */}
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

        <div
          ref={trackRef}
          className="flex gap-3 trending-marquee"
          style={{ animationPlayState: paused ? "paused" : "running" }}
        >
          {loopStocks.map((stock, i) => {
            const style = getSentimentStyle(stock.sentiment);
            const Icon = style.icon;
            const changeSign = stock.change_percent >= 0 ? "+" : "";
            return (
              <Link
                key={`${stock.ticker}-${i}`}
                to={`/stocks/${tickerToSlug(stock.ticker)}`}
                className="shrink-0 w-[200px] rounded-xl border border-border bg-card/60 px-4 py-3 hover:border-gold/40 hover:bg-card/80 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-display font-bold text-foreground">{stock.ticker}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{stock.company_name}</div>
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${style.bg} ${style.color}`}>
                    <Icon className="w-3 h-3" />
                    {changeSign}{Number(stock.change_percent).toFixed(2)}%
                  </div>
                </div>
                <div className="mt-2 text-lg font-display font-bold text-foreground">
                  ${Number(stock.price).toFixed(2)}
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes trending-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .trending-marquee {
          width: max-content;
          animation: trending-scroll 40s linear infinite;
        }
      `}</style>
    </section>
  );
};

function getSentimentStyle(sentiment: string) {
  const s = sentiment?.toLowerCase();
  if (s === "bullish") return { icon: TrendingUp, color: "text-green-400", bg: "bg-green-500/10 border-green-500/20" };
  if (s === "bearish") return { icon: TrendingDown, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" };
  return { icon: Minus, color: "text-muted-foreground", bg: "bg-muted/50 border-border" };
}

function determineSentiment(changePercent: number): string {
  if (changePercent > 1) return "Bullish";
  if (changePercent < -1) return "Bearish";
  return "Neutral";
}

export default TrendingStocksWidget;
